import { FC, useEffect } from 'react';
import {
  Button,
  CodeEditor,
  type Extension,
  TextSubtitle,
  Stack,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';

import { GenericModal, GenericModalProps } from '../..';

import './editor-modal.scss';

export type EditorModalProps = GenericModalProps & {
  code?: string;
  title?: string;
  langs?: Extension[];
  progress?: number;
  loading?: boolean;
  viewOnly?: boolean;
  onSave?: (code: string) => void;
  /**
   * Fed by the editor on every keystroke. Supplied by `useEditorModal`, which
   * owns the draft — see the note there (PSTUDIO-3897) for why the content must
   * not live inside the web component.
   */
  onCodeChange?: (code: string) => void;
};

export const EditorModal: FC<EditorModalProps> = ({
  modalRef,
  code,
  title,
  langs,
  loading,
  progress,
  viewOnly,
  onClose,
  onSave,
  onCodeChange,
}) => {
  useEffect(() => {
    if (progress && progress >= 100 && !loading) {
      onClose?.();
    }
  }, [progress, loading, onClose]);

  // `code` IS the live draft (controlled by `useEditorModal`), so it can be
  // saved directly. Reading the web component back through a ref was how a
  // prop-driven revert used to slip into the persisted value.
  const handleSave = () => {
    onSave?.(code ?? '');
    onClose?.();
  };

  return (
    <GenericModal
      test-id="editor-modal"
      modalRef={modalRef}
      size="large"
      hideCloseBtn
      stayOnClickOutside
      stayOnEsc
      expandable
      className="c-editor-modal"
    >
      <TextSubtitle size="small" slot="title">
        {title}
      </TextSubtitle>

      <CodeEditor
        extensions={langs}
        value={code || ''}
        onChange={onCodeChange}
        height="calc(100vh - 124px)"
        theme="vscodeDark"
        disabled={loading || viewOnly}
      />

      <Stack justifyContent="end" gutter="small" slot="footer">
        <Button
          test-id="ps-editor-modal-cancel"
          size="large"
          variant="secondary"
          onClick={onClose}
          disabled={loading}
        >
          <TranslationComponent i18nKey="pro-studio/actions:editorModal.cancel">
            Cancel
          </TranslationComponent>
        </Button>
        {!viewOnly && (
          <Button
            test-id="ps-editor-modal-save"
            size="large"
            onClick={handleSave}
            disabled={loading}
          >
            <TranslationComponent
              i18nKey={
                loading && progress
                  ? 'pro-studio/actions:editorModal.save.progress'
                  : 'pro-studio/actions:editorModal.save.label'
              }
            >
              {loading ? 'Saving' : 'Save'}
            </TranslationComponent>
            {loading && progress && `: ${progress}%`}
          </Button>
        )}
      </Stack>
    </GenericModal>
  );
};
